import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  clampPageNumber,
  pageCountFor,
  parsePageNumber,
  parsePageSize,
  type ChinaPaymentRecord,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, EmptyState, Icon, cn } from '@mypoz/ui';

import { Pager } from '@/components/Pager.js';
import { CancelReason } from '@/features/china-payment/components/CancelReason.js';
import { RecordStatus, RecordType } from '@/features/china-payment/components/RecordStatus.js';
import { RequestDetailsSheet } from '@/features/china-payment/components/RequestDetailsSheet.js';
import { useChinaPaymentRecords } from '@/features/china-payment/useChinaPaymentRecords.js';
import { useFormat } from '@/lib/format.js';
import { buttonClass } from '@/lib/styles.js';
import { CHINA_PAYMENT_ROUTE } from '@/shell/navigation.js';

/**
 * The China Payment request history (MYP-63): the old website's 支付币兑换 → 充值记录, from
 * `getalipaylist`, a page at a time (`?page=`, `?size=`). A table from `lg`, with the old
 * website's columns; a card per request on a phone. A cancelled request offers staff's reason
 * ("Why?"), and every request opens its details (`?request=<id>`), as the old ✎ did.
 *
 * ALIPAY notifications open this page (MYP-69).
 */
export function TopUpRecords() {
  const { t } = useTranslation();
  const { t: tNav } = useTranslation('nav');
  const [searchParams, setSearchParams] = useSearchParams();
  const top = useRef<HTMLDivElement>(null);

  const requestedPage = parsePageNumber(searchParams);
  const pageSize = parsePageSize(searchParams);
  const openRequest = searchParams.get('request');

  const { status, page: answer, retry } = useChinaPaymentRecords(requestedPage, pageSize);
  const records = answer?.records ?? [];

  // `count` has been one too high on this vendor's other lists: a short page is the last one, and
  // says exactly how many there are.
  const total =
    answer === undefined
      ? 0
      : records.length < pageSize
        ? (requestedPage - 1) * pageSize + records.length
        : Math.max(answer.total, requestedPage * pageSize);
  const pageCount = pageCountFor(total, pageSize);
  const page = clampPageNumber(requestedPage, total, pageSize);

  // A bookmarked `?page=` past the end (an empty page beyond page 1) goes to the last page there is.
  useEffect(() => {
    if (status !== 'ready' || answer === undefined) return;
    if (records.length > 0 || requestedPage <= 1 || answer.total === 0) return;
    const last = pageCountFor(answer.total, pageSize);
    if (last >= requestedPage) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (last <= 1) params.delete('page');
        else params.set('page', String(last));
        return params;
      },
      { replace: true },
    );
  }, [status, answer, records.length, requestedPage, pageSize, setSearchParams]);

  const update = (change: (params: URLSearchParams) => void) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      change(params);
      return params;
    });

  const goToPage = (next: number) => {
    update((params) => {
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
    });
    top.current?.scrollIntoView({ block: 'start' });
  };
  const changePageSize = (size: number) =>
    update((params) => {
      params.set('size', String(size));
      params.delete('page');
    });
  const openDetails = (id: string) => update((params) => params.set('request', id));
  const closeDetails = () => update((params) => params.delete('request'));

  const firstLoad = status === 'loading' && answer === undefined;
  const failed = status === 'error';
  const empty = status === 'ready' && total === 0;

  return (
    <div ref={top} className="min-w-0 scroll-mt-24">
      <div className="mb-4 flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-ink">{tNav('topUpRecords')}</h1>
          <p className="mt-1 hidden text-sm text-muted sm:block">
            {t('chinaPayment.records.subtitle')}
          </p>
        </div>
        <Link to={CHINA_PAYMENT_ROUTE} className={buttonClass('primary', 'shrink-0')}>
          <Icon name="add" size={18} />
          {t('chinaPayment.records.newRequest')}
        </Link>
      </div>

      {empty ? (
        <EmptyState
          icon="receipt_long"
          title={t('chinaPayment.records.emptyTitle')}
          description={t('chinaPayment.records.emptyBody')}
        />
      ) : (
        <div className="grid min-w-0 gap-4">
          <Card className="min-w-0 p-0">
            {firstLoad ? (
              <p aria-busy="true" className="px-5 py-10 text-center text-sm text-muted sm:px-6">
                {t('chinaPayment.records.loading')}
              </p>
            ) : failed ? (
              <div className="grid justify-items-center gap-3 px-5 py-10 sm:px-6">
                <p className="text-center text-sm text-ink">{t('chinaPayment.records.failed')}</p>
                <button type="button" onClick={retry} className={buttonClass('secondary')}>
                  <Icon name="refresh" size={18} />
                  {t('actions.retry')}
                </button>
              </div>
            ) : (
              <div
                aria-busy={status === 'loading'}
                className={cn(status === 'loading' && 'opacity-60 transition-opacity')}
              >
                <RecordsTable records={records} onOpen={openDetails} />
                <ul className="divide-y divide-line lg:hidden">
                  {records.map((record) => (
                    <RecordItem key={record.id} record={record} onOpen={openDetails} />
                  ))}
                </ul>
              </div>
            )}
          </Card>

          {status === 'ready' && total > 0 ? (
            <Pager
              page={page}
              pageCount={pageCount}
              rangeStart={(page - 1) * pageSize + 1}
              rangeEnd={(page - 1) * pageSize + records.length}
              total={total}
              onGo={goToPage}
              label={t('chinaPayment.records.pagerLabel')}
              pageSize={pageSize}
              onPageSize={changePageSize}
            />
          ) : null}
        </div>
      )}

      {openRequest ? <RequestDetailsSheet id={openRequest} onClose={closeDetails} /> : null}
    </div>
  );
}

interface RowProps {
  record: ChinaPaymentRecord;
  onOpen: (id: string) => void;
}

/** Laptop: the old website's columns, in its order, then the details button (its ✎). */
function RecordsTable({
  records,
  onOpen,
}: {
  records: readonly ChinaPaymentRecord[];
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const head =
    'px-2.5 py-2.5 text-left text-xs font-semibold whitespace-nowrap text-muted first:pl-6 last:pr-4';
  const cell = 'px-2.5 py-3 align-middle whitespace-nowrap first:pl-6 last:pr-4';

  return (
    <div className="relative hidden overflow-x-auto lg:block">
      <table className="w-full text-sm">
        <thead className="border-b border-line bg-surface-alt">
          <tr>
            <th scope="col" className={head}>
              {t('chinaPayment.records.columns.time')}
            </th>
            <th scope="col" className={head}>
              {t('chinaPayment.records.columns.reference')}
            </th>
            <th scope="col" className={head}>
              {t('chinaPayment.records.columns.bank')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('chinaPayment.records.columns.rate')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('chinaPayment.records.columns.rmAmount')}
            </th>
            <th scope="col" className={cn(head, 'text-right')}>
              {t('chinaPayment.records.columns.rmbAmount')}
            </th>
            <th scope="col" className={head}>
              {t('chinaPayment.records.columns.type')}
            </th>
            <th scope="col" className={head}>
              {t('chinaPayment.records.columns.status')}
            </th>
            <th scope="col" className={head}>
              <span className="sr-only">{t('chinaPayment.records.columns.details')}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {records.map((record) => (
            <tr key={record.id}>
              {/* Date and time (and the type) may break onto two lines, so nine columns fit a laptop. */}
              <td className={cn(cell, 'min-w-32 whitespace-normal text-muted tabular-nums')}>
                {record.submittedAt ? fmt.dateTime(record.submittedAt) : '—'}
              </td>
              <td className={cn(cell, 'tabular-nums text-ink')}>{record.reference ?? '—'}</td>
              <td className={cn(cell, 'text-muted')}>{record.bankName ?? '—'}</td>
              <td className={cn(cell, 'text-right text-muted tabular-nums')}>
                {record.rate ? fmt.number(record.rate.value, record.rate.decimals) : '—'}
              </td>
              <td className={cn(cell, 'text-right text-ink tabular-nums')}>
                {record.rmCents === undefined ? '—' : fmt.money(record.rmCents, 'RM')}
              </td>
              <td className={cn(cell, 'text-right font-semibold text-ink tabular-nums')}>
                {record.rmbCents === undefined ? '—' : fmt.money(record.rmbCents, 'RMB')}
              </td>
              <td className={cn(cell, 'min-w-28 whitespace-normal text-ink')}>
                <RecordType record={record} />
              </td>
              <td className={cell}>
                {/* "Why?" sits under the chip, not beside it, to keep the column narrow. */}
                <span className="inline-flex flex-col items-start gap-0.5">
                  <RecordStatus record={record} />
                  {record.status === 'cancelled' ? (
                    <CancelReason reference={record.reference} reason={record.cancelReason} />
                  ) : null}
                </span>
              </td>
              <td className={cn(cell, 'text-right')}>
                <DetailsButton record={record} onOpen={onOpen} compact />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phone: what and how much first, then when and where, then the status and details. */
function RecordItem({ record, onOpen }: RowProps) {
  const fmt = useFormat();

  return (
    <li className="grid gap-1.5 px-5 py-4 sm:px-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 text-sm font-semibold text-ink">
          <RecordType record={record} />
        </p>
        <span className="shrink-0 text-sm font-semibold text-ink tabular-nums">
          {record.rmbCents === undefined ? '—' : fmt.money(record.rmbCents, 'RMB')}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-xs text-muted tabular-nums">
        <span className="min-w-0">
          {[record.submittedAt ? fmt.dateTime(record.submittedAt) : undefined, record.bankName]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="shrink-0">
          {record.rmCents === undefined ? '' : fmt.money(record.rmCents, 'RM')}
        </span>
      </div>
      {record.reference ? (
        <p className="text-xs text-muted tabular-nums">{record.reference}</p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-x-3">
        <span className="inline-flex items-center gap-1">
          <RecordStatus record={record} />
          {record.status === 'cancelled' ? (
            <CancelReason reference={record.reference} reason={record.cancelReason} />
          ) : null}
        </span>
        <DetailsButton record={record} onOpen={onOpen} />
      </div>
    </li>
  );
}

function DetailsButton({ record, onOpen, compact = false }: RowProps & { compact?: boolean }) {
  const { t } = useTranslation();
  const label = record.reference
    ? t('chinaPayment.records.detailsFor', { reference: record.reference })
    : t('chinaPayment.records.details');
  return (
    <button
      type="button"
      onClick={() => onOpen(record.id)}
      aria-label={label}
      // The laptop table has nine columns: there the button is the icon alone (the old ✎), named
      // for screen readers and on hover; a phone card has room for the word.
      title={compact ? label : undefined}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 rounded-lg text-sm font-semibold text-brand',
        'transition-colors hover:bg-brand-soft',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        compact ? 'size-9 min-h-9 justify-center' : 'px-2',
      )}
    >
      <Icon name="description" size={20} />
      {compact ? null : t('chinaPayment.records.details')}
    </button>
  );
}
